import React, { useMemo } from "react";
import { useBoltDocumentTemplateForm } from "./useBoltDocumentTemplateForm";
import {
  ActionButtons,
  AppToasterV2,
  dialogStore,
  FileField,
  Footer,
  LoadingV2,
  TextField,
} from "@ucl/library";
import { useBoltApiClient } from "@bolt/core";
import { BoltDocumentTemplateFormProps } from "./BoltDocumentTemplateFormCreate";
import { faTrash } from "@fortawesome/free-solid-svg-icons";

export interface BoltDocumentTemplateFormUpdateProps
  extends BoltDocumentTemplateFormProps {
  templateId: string;
  onDelete?: () => void;
}

export const BoltDocumentTemplateFormUpdate: React.FC<
  BoltDocumentTemplateFormUpdateProps
> = (props) => {
  const boltApiClient = useBoltApiClient();

  const {
    updateTemplate,
    deleteTemplate,
    isSaving,
    isLoading,
    templateName,
    textFieldProps,
    fileFieldProps,
  } = useBoltDocumentTemplateForm(props, boltApiClient, props.templateId);

  // Memoize the components
  const TemplateFileField = useMemo(() => React.memo(FileField), []);

  return (
    <section className="bolt-document-template-form">
      {isLoading && <LoadingV2 />}
      {boltApiClient && !isLoading && (
        <div className="bolt-document-template-form-content">
          <TextField {...textFieldProps} value={templateName} />
          <TemplateFileField
            {...fileFieldProps}
            label="Replace Template File"
          />
        </div>
      )}
      <Footer>
        <ActionButtons<{ templateName: string }>
          data={{
            templateName,
          }}
          actions={[
            {
              iconProps: {
                icon: faTrash,
              },
              position: "left",
              isPrimary: false,
              color: "sunset-red",
              isDisabled: (data) => !data?.templateName || isSaving,
              isLoading: isSaving,
              onActionClick: async () => {
                dialogStore.openConfirmDialog({
                  dialogId: "delete-template",
                  content: "Are you sure you want to delete this template?",
                  confirmButtonText: "Delete",
                  color: "sunset-red",
                  onConfirm: async () => {
                    const result = {
                      isSuccess: true,
                      error: undefined,
                    };

                    await deleteTemplate()
                      .then(() => {
                        props.onDelete?.();
                        AppToasterV2.showSuccessAppToaster("Template deleted.");
                      })
                      .catch((error) => {
                        console.error(error);
                        result.isSuccess = false;
                        result.error = error;
                      });

                    return result;
                  },
                });
              },
            },
            {
              text: "Cancel",
              isDisabled: (_data) => isSaving,
              onActionClick: props.onCancel,
            },
            {
              text: "Save",
              isPrimary: true,
              isDisabled: (data) => !data?.templateName,
              isLoading: isSaving,
              onActionClick: () => {
                updateTemplate();
                props.onSave?.();
              },
            },
          ]}
        />
      </Footer>
    </section>
  );
};
